<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Acquirenti prodotti in lista</h1>
          <p class="subtitle">Utenti che hanno acquistato almeno {{ minDistinct }} prodotti distinti della lista (solo ordini pagati).</p>
        </div>

        <section class="card-surface filters-card">
          <div class="filters-row">
            <div class="filters-group">
              <label>Prodotti considerati</label>
              <div class="chips">
                <span v-for="label in productList" :key="label" class="chip">
                  {{ label }}
                </span>
              </div>
            </div>
            <div class="filters-actions">
              <button type="button" class="btn primary staff-button-neutral" @click="loadOrders" :disabled="loading">
                {{ loading ? 'Caricamento…' : 'Aggiorna' }}
              </button>
            </div>
          </div>
          <p v-if="lastUpdatedAt" class="filters-note">
            Ultimo aggiornamento: {{ formatDateTime(lastUpdatedAt) }} · Ordini analizzati: {{ processedOrders }}
          </p>
        </section>

        <section class="card-surface" v-if="error">
          <p class="error-state">{{ error }}</p>
        </section>

        <section class="card-surface" v-else-if="loading">
          <p class="muted">Caricamento ordini…</p>
        </section>

        <section class="card-surface" v-else>
          <div class="summary-row">
            <div><strong>Utenti trovati:</strong> {{ users.length }}</div>
            <div><strong>Prodotti minimi:</strong> {{ minDistinct }}</div>
          </div>

          <table v-if="users.length" class="lines-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Prodotti trovati</th>
                <th>N. ordini</th>
                <th>Ultima data</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in users" :key="row.email">
                <td class="mono">{{ row.email }}</td>
                <td>
                  <div class="chips">
                    <span v-for="label in row.products" :key="`${row.email}-${label}`" class="chip chip--light">
                      {{ label }}
                    </span>
                  </div>
                </td>
                <td>{{ row.orderCount }}</td>
                <td>{{ row.lastOrderLabel }}</td>
              </tr>
            </tbody>
          </table>

          <p v-else class="muted">Nessun utente trovato.</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { getFirestore, collection, getDocs, query, orderBy, startAfter, limit } from 'firebase/firestore';
import { resolveOrderTimeMs } from '@/lib/topSku';
import SidebarComponent from '../SidebarComponent.vue';

const ORDER_PAGE_SIZE = 400;
const MIN_DISTINCT = 2;
const PRODUCT_LIST = [
  'FC 4 plus',
  'FC 6 plus',
  'Comm - FC 4 +4',
  'Comm - FC 6 +6',
  'Fc 6+4',
  'Fc 4+4',
  'Fc 4+2',
];

const normalizeKey = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '')
  .trim();

const PRODUCT_MATCHERS = PRODUCT_LIST
  .map((label, idx) => ({ label, key: normalizeKey(label), index: idx }))
  .filter((item) => item.key)
  .sort((a, b) => b.key.length - a.key.length);

const PRODUCT_ORDER = new Map(PRODUCT_LIST.map((label, idx) => [label, idx]));

export default {
  name: 'AcquirentiProdottiLista',
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const users = ref([]);
    const loading = ref(false);
    const error = ref('');
    const processedOrders = ref(0);
    const lastUpdatedAt = ref(null);

    const resolveOrderEmail = (order = {}) => {
      const candidates = [
        order.userEmail,
        order.customerEmail,
        order.email,
        order.customer_email,
        order.billing?.email,
      ];
      for (const candidate of candidates) {
        if (candidate) return String(candidate).trim().toLowerCase();
      }
      return '';
    };

    const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

    const isPaidOrder = (order = {}) => {
      const statusCandidates = [
        order.status,
        order.statusOrder,
        order.orderStatus,
        order.paymentStatus,
        order.payment_status,
      ];
      const rawStatus = statusCandidates.map(normalizeStatus).find(Boolean) || '';
      if (!rawStatus) return false;

      const paidSet = new Set([
        'pagato',
        'paid',
        'completed',
        'complete',
        'processing',
        'wc-processing',
        'wc-completed',
        'direct purchase',
        'direct_purchase',
        'payment_completed',
        'completed_payment',
      ]);
      if (paidSet.has(rawStatus)) return true;
      if (rawStatus.startsWith('wc-') && (rawStatus.includes('processing') || rawStatus.includes('completed'))) return true;
      if (rawStatus.includes('direct') && rawStatus.includes('purchase')) return true;
      if (rawStatus.includes('payment') && rawStatus.includes('complete')) return true;
      return false;
    };

    const resolveLineName = (line = {}) => {
      const candidates = [
        line.name,
        line.title,
        line.productName,
        line.product_name,
        line.product_title,
        line.item_name,
        line.itemName,
      ];
      for (const candidate of candidates) {
        if (candidate) return String(candidate);
      }
      return '';
    };

    const safeProducts = (order) => {
      const candidates = [order?.products, order?.line_items, order?.lineItems, order?.items];
      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) return candidate;
      }
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    };

    const resolveMatchedLabel = (lineName) => {
      const key = normalizeKey(lineName);
      if (!key) return null;
      for (const item of PRODUCT_MATCHERS) {
        if (key.includes(item.key)) return item.label;
      }
      return null;
    };

    const formatDateTime = (value) => {
      if (!value) return '—';
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('it-IT');
    };

    const formatDateFromMs = (ms) => {
      if (!ms) return '—';
      const date = new Date(ms);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('it-IT');
    };

    const loadOrders = async () => {
      if (loading.value) return;
      loading.value = true;
      error.value = '';
      processedOrders.value = 0;
      users.value = [];

      const usersMap = new Map();

      try {
        const ordersRef = collection(db, 'orders');
        let lastDoc = null;
        let hasMore = true;

        while (hasMore) {
          const q = lastDoc
            ? query(ordersRef, orderBy('__name__'), startAfter(lastDoc), limit(ORDER_PAGE_SIZE))
            : query(ordersRef, orderBy('__name__'), limit(ORDER_PAGE_SIZE));
          const snap = await getDocs(q);
          if (snap.empty) break;

          snap.forEach((docSnap) => {
            processedOrders.value += 1;
            const data = docSnap.data() || {};
            if (!isPaidOrder(data)) return;
            const email = resolveOrderEmail(data);
            if (!email) return;

            let lines = safeProducts(data);
            if (!lines.length) {
              const fallbackName = data.productName || data.product_title || data.productTitle || data.name || data.title;
              if (fallbackName) {
                lines = [{ name: fallbackName }];
              }
            }

            const orderMatches = new Set();
            for (const line of lines) {
              const name = resolveLineName(line);
              if (!name) continue;
              const matchLabel = resolveMatchedLabel(name);
              if (matchLabel) orderMatches.add(matchLabel);
            }

            if (!orderMatches.size) return;

            let entry = usersMap.get(email);
            if (!entry) {
              entry = {
                productsSet: new Set(),
                ordersSet: new Set(),
                lastOrderMs: 0,
              };
              usersMap.set(email, entry);
            }

            orderMatches.forEach((label) => entry.productsSet.add(label));
            entry.ordersSet.add(docSnap.id);

            const orderDateMs = resolveOrderTimeMs(data) || 0;
            if (orderDateMs && orderDateMs > entry.lastOrderMs) {
              entry.lastOrderMs = orderDateMs;
            }
          });

          lastDoc = snap.docs[snap.docs.length - 1];
          if (snap.size < ORDER_PAGE_SIZE) {
            hasMore = false;
          }
        }

        const result = Array.from(usersMap.entries())
          .map(([email, entry]) => {
            const products = Array.from(entry.productsSet).sort((a, b) => {
              const ai = PRODUCT_ORDER.has(a) ? PRODUCT_ORDER.get(a) : 9999;
              const bi = PRODUCT_ORDER.has(b) ? PRODUCT_ORDER.get(b) : 9999;
              if (ai !== bi) return ai - bi;
              return a.localeCompare(b, 'it', { sensitivity: 'base' });
            });
            return {
              email,
              products,
              productCount: entry.productsSet.size,
              orderCount: entry.ordersSet.size,
              lastOrderMs: entry.lastOrderMs,
              lastOrderLabel: formatDateFromMs(entry.lastOrderMs),
            };
          })
          .filter((row) => row.productCount >= MIN_DISTINCT)
          .sort((a, b) => {
            if (b.productCount !== a.productCount) return b.productCount - a.productCount;
            if (b.lastOrderMs !== a.lastOrderMs) return b.lastOrderMs - a.lastOrderMs;
            return a.email.localeCompare(b.email, 'it', { sensitivity: 'base' });
          });

        users.value = result;
        lastUpdatedAt.value = new Date();
      } catch (err) {
        console.error('Errore caricamento ordini:', err);
        error.value = 'Errore durante il caricamento degli ordini.';
      } finally {
        loading.value = false;
      }
    };

    onMounted(loadOrders);

    return {
      users,
      loading,
      error,
      processedOrders,
      lastUpdatedAt,
      minDistinct: MIN_DISTINCT,
      productList: PRODUCT_LIST,
      loadOrders,
      formatDateTime,
    };
  },
};
</script>

<style scoped>
.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 20px 24px;
  margin-top: 18px;
}

.filters-card {
  margin-top: 24px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: flex-end;
}

.filters-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 320px;
}

.filters-group label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.filters-actions {
  margin-left: auto;
  display: flex;
  gap: 10px;
}

.filters-note {
  margin-top: 12px;
  color: #64748b;
  font-size: 0.85rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #ffffff;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 16px;
  font-size: 0.95rem;
  color: #0f172a;
}

.lines-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}

.lines-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.lines-table td {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 600;
}

.chip--light {
  background: #e2e8f0;
  color: #0f172a;
}

.mono {
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.muted {
  color: #64748b;
}

.error-state {
  color: #b91c1c;
  font-weight: 600;
}

.subtitle {
  color: #64748b;
  margin-top: 6px;
}
</style>
